import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableHead,
  Typography,
} from "@material-ui/core";
import { useBalances } from "../../../hooks/useBalances";
import { useEquity } from "../../../hooks/useEquity";
import {C, GridDivider, H, H32, R} from "./styled";
import { useDeposit } from "../../../hooks/useDeposit";
import { useCredit } from "../../../hooks/useCredit";

const DISABLE_MARGIN_EQUITY = true  // Margin equity calculation is incorrect, but may be correct in the future
const BROKER_NAME           = process.env.REACT_APP_BROKER_NAME || "";

export type ClientFundingPageProps = {
  portfolioId: number;
  clientId?: number;
};

const stringToFixedNumber = (number: string, decimalPlaces: number): string => {
  return parseFloat(number).toFixed(decimalPlaces)
}

export const ClientFunding = (p: ClientFundingPageProps) => {
  const { balances, loadBalances }  = useBalances(),
        { equity, loadEquity }      = useEquity(),
        { credit, loadCredit }      = useCredit(p.clientId), //do i need to push the client id here as an argument?s
        { deposits, loadDeposits }  = useDeposit(p.clientId);

  useEffect(() => {
    loadBalances(p.portfolioId).catch(console.error);
  }, [loadBalances, p.portfolioId]);

  useEffect(() => {
    loadEquity(p.portfolioId);
  }, [loadEquity, p.portfolioId]);

  useEffect(() => {
    loadCredit(p.clientId).catch(console.error);;
  }, [loadCredit, p.clientId]);

  useEffect(() => {
    loadDeposits(p.clientId).catch(console.error);;
  }, [loadDeposits, p.clientId]);

  let wire_instructions;
  if (BROKER_NAME === "blockfills") {
      wire_instructions =
          <TableBody>
              <R>
                  <C> Account Title: </C>
                  <C> Reliz LTD</C>
              </R>
              <R>
                  <C>Account Address: </C>
                  <C>401 W. Ontario ST. STE 400; Chicago, IL 60654-6957</C>
              </R>
              <R>
                  <C>Routing/ABA: </C>
                  <C>026013576</C>
              </R>
              <R>
                  <C>Swift:</C>
                  <C>SIGNUS33XXX</C>
              </R>
              <R>
                  <C>Bank Address:</C>
                  <C>565 Fifth Avenue; New York, NY 10017</C>
              </R>
              <R>
                  <C>Account#</C>
                  <C>1503509241</C>
              </R>
          </TableBody>;
  } else if(BROKER_NAME === "xerof") {
      wire_instructions =
          <TableBody>
              <R>
                  <C> Bank Name: </C>
                  <C> Maerki Baumann & Co. AG</C>
              </R>
              <R>
                  <C>Account Name: </C>
                  <C>FE Swiss Financial AG</C>
              </R>
              <R>
                  <C>USD IBAN: </C>
                  <C>cH93 0857 3102 4302 0233 3</C>
              </R>
              <R>
                  <C>EUR IBAN: </C>
                  <C>cH41 0857 3102 4302 0181 4</C>
              </R>
              <R>
                  <C>Bank clearing:</C>
                  <C>8573</C>
              </R>
              <R>
                  <C>Swift:</C>
                  <C>MAEBCHZZ</C>
              </R>
              <R>
                  <C>Bank Address:</C>
                  <C>Dreikönigstrasse 6, CH-8002 Zürich</C>
              </R>
          </TableBody>
  } else {
    wire_instructions = <TableBody/>
  }

  return (
    <>
      <Grid container spacing={4}>
        <GridDivider item xs={12} lg={6}>
          {equity && (
            <>
              <Box mt={2} mb={3}>
                <Typography variant="h5">Equity & funds</Typography>
              </Box>
              <Table size="small">
              {credit.level!=="ERROR" && <>
                  <TableHead>
                    <R>
                      <H32>Credit</H32>
                    </R>
                  </TableHead>
                  <TableBody>
                  <R>
                    <C> Limit: </C>
                    <C> {credit.limit} </C>
                  </R>
                  <R>
                    <C> Exposure: </C>
                    <C> {Number((credit.exposure).toFixed(2))} </C>
                  </R>
                  <R>
                    <C> Utilization: </C>
                    <C> {Number((credit.utilization).toFixed(2))} </C>
                  </R>
                  <R>
                    <C> Level: </C>
                    <C> {credit.level} </C>
                  </R>
                  </TableBody>
                </>}
                {equity.client_portfolio && <>
                  <TableHead>
                    <R>
                      <H32>Equity</H32>
                    </R>
                  </TableHead>
                  <TableBody>
                    <R>
                      <C>
                        {stringToFixedNumber(equity.client_portfolio.balance, 2) === '-0.00' ? '0.00' : stringToFixedNumber(equity.client_portfolio.balance, 2)} {equity.client_portfolio.currency}
                      </C>
                    </R>
                  </TableBody>
                </>}
                {equity.client_cash_portfolio && <>
                  <TableHead>
                    <R>
                      <H32>Cash Equity</H32>
                    </R>
                  </TableHead>
                  <TableBody>
                    <R>
                      <C>
                        {stringToFixedNumber(equity.client_cash_portfolio.balance, 2) === '-0.00' ? '0.00' : stringToFixedNumber(equity.client_cash_portfolio.balance, 2)} {equity.client_cash_portfolio.currency}
                      </C>
                    </R>
                  </TableBody>
                </>}
                {!DISABLE_MARGIN_EQUITY && equity.client_margin_portfolio && <>
                  <TableHead>
                    <R>
                      <H32>Margin Equity</H32>
                    </R>
                  </TableHead>
                  <TableBody>
                    <R>
                      <C>
                        {stringToFixedNumber(equity.client_margin_portfolio.balance, 2) === '-0.00' ? '0.00' : stringToFixedNumber(equity.client_margin_portfolio.balance, 2)} {equity.client_margin_portfolio.currency}
                      </C>
                    </R>
                  </TableBody>
                </>}
              </Table>
            </>
          )}
          {balances.client_portfolio && <>
              <Box mt={2} mb={3}>
                <Typography variant="h5">Balances</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <R>
                    <H32>Currency</H32>
                    <H>Amount</H>
                  </R>
                </TableHead>
                <TableBody>
                  {balances.client_portfolio.map((b) => (
                    <R key={'funding-'+b.currency}>
                      <C>{b.currency}</C>
                      <C>{b.balance}</C>
                    </R>
                  ))}
                </TableBody>
              </Table>
            </>
          }
          {balances.client_cash_portfolio && <>
              <Box mt={2} mb={3}>
                <Typography variant="h5">Cash Balances</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <R>
                    <H32>Currency</H32>
                    <H>Amount</H>
                  </R>
                </TableHead>
                <TableBody>
                  {balances.client_cash_portfolio.map((b) => (
                    <R key={'cash-funding-'+b.currency}>
                      <C>{b.currency}</C>
                      <C>{b.balance}</C>
                    </R>
                  ))}
                </TableBody>
              </Table>
            </>
          }
          {balances.client_margin_portfolio && <>
              <Box mt={2} mb={3}>
                <Typography variant="h5">Margin Balances</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <R>
                    <H32>Currency</H32>
                    <H>Amount</H>
                  </R>
                </TableHead>
                <TableBody>
                  {balances.client_margin_portfolio.map((b) => (
                    <R key={'margin-funding-'+b.currency}>
                      <C>{b.currency}</C>
                      <C>{b.balance}</C>
                    </R>
                  ))}
                </TableBody>
              </Table>
            </>
          }
        </GridDivider>
        <Grid item xs={12} lg={6}>
          {deposits && (
            <>
              <Box mt={2} mb={3}>
                <Typography variant="h5">Deposits</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <R>
                    <H32>Currency</H32>
                    <H>Address</H>
                  </R>
                </TableHead>
                <TableBody>
                  {deposits?.map((d) => (
                    <R key={'asset-'+d.assetName}>
                      <C>{d.assetName}</C>
                      <C>{d.address}</C>
                    </R>
                  ))}
                </TableBody>
              </Table>
              <Box mt={2} mb={3}>
                <Typography variant="h5">Wire Instructions</Typography>
              </Box>
              <Table size="small">
                  {wire_instructions}
              </Table>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};
